import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Hammer, Star, Check, MapPin, Phone, ChevronRight } from "lucide-react";
import { Meta, Schema } from "../components/SEO";
import { serviceCategories, services, servicesGrouped } from "../data/services";
import { cities } from "../data/cities";
import { usePublicProjects } from "../hooks/useProjects";
import { generalFaqs } from "../data/faqs";
import { reviews } from "../data/reviews";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { LeadMagnetForm } from "../components/LeadMagnetForm";
import { SmartImage } from "../components/SmartImage";
import { useGoogleReviews, normalizeGoogleReview } from "../hooks/useGoogleReviews";
import { company } from "../data/company";

// Echte projectfoto: complete woningrenovatie Delfshaven (eigen project, Cloudinary)
const heroImage = "https://res.cloudinary.com/dz15hlu0u/image/upload/v1785106635/koning/projecten/file_lu7dcj.jpg";

const trust = [
  { icon: Award, label: "VCA-gecertificeerde medewerkers" },
  { icon: Hammer, label: "Vast uitvoeringsteam" },
  { icon: Star, label: "Beoordeeld op Google" },
];

const processSteps = [
  { num: "01", title: "Adviesgesprek", desc: "We starten met een vrijblijvend adviesgesprek waarin we uw wensen, planning en budget bespreken. Persoonlijk en zonder verkooppraat.", variant: "orange" },
  { num: "02", title: "Ontwerp & Offerte", desc: "U ontvangt een helder ontwerp met vaste totaalprijs, materialen, planning en beoogde opleverdatum. Wijzigingen en meerwerk worden altijd vooraf schriftelijk vastgelegd.", variant: "tan" },
  { num: "03", title: "Uitvoering", desc: "Ons vaste uitvoeringsteam voert het werk uit volgens planning. Dagelijks contact, strakke afwerking en oplevering met garantie.", variant: "navy" },
];

const usps = [
  { icon: ShieldCheck, t: "Vaste totaalprijs", d: "U weet vooraf waar u aan toe bent. Wijzigingen en meerwerk alleen na uw schriftelijk akkoord." },
  { icon: Hammer, t: "Vast uitvoeringsteam", d: "Vast, ervaren team van begin tot oplevering. Voor specialistisch werk werken wij met vertrouwde specialistische partners." },
  { icon: Award, t: "VCA-gecertificeerde medewerkers", d: "Veilig en vakkundig werken volgens de VCA-veiligheidsnormen, voor zowel particulier als zakelijk werk." },
  { icon: Star, t: "Beoordeeld op Google", d: "Onze Google-score wordt live opgehaald. Lees de ervaringen van onze klanten op Google en Werkspot." },
];

export default function HomePage() {
  const { allProjects } = usePublicProjects();
  const live = useGoogleReviews();
  const liveConfigured = !!live?.configured;
  const liveItems = liveConfigured ? (live.reviews || []).map(normalizeGoogleReview) : [];
  // Prefer live Google reviews first, then curated ones to fill the grid (dedupe by author)
  const seenNames = new Set(liveItems.map((r) => r.name.toLowerCase()));
  const mergedReviews = [
    ...liveItems,
    ...reviews.filter((r) => !seenNames.has(r.name.toLowerCase())),
  ].sort((a, b) => (b.rating || 5) - (a.rating || 5));
  const displayRating = liveConfigured && live.rating ? live.rating.toFixed(1) : null;
  // Only display a review count when the live Google API returns a real number.
  // Never sum Google + Werkspot or fall back to a hard-coded aggregate.
  const displayCount = liveConfigured && live.userRatingCount ? live.userRatingCount : null;

  const featuredServices = ["badkamer-renovatie", "keuken-renovatie", "aanbouw", "complete-woning-renovatie", "kunststof-kozijnen", "dakkapel"]
    .map((slug) => services.find((s) => s.slug === slug))
    .filter(Boolean);

  return (
    <>
      <Meta
        title="Aannemer voor complete woningrenovaties in Zuid-Holland"
        description="Aannemer in Maassluis voor renovatie en verbouwing in Zuid-Holland. Vaste prijs en vast uitvoeringsteam. Vraag vrijblijvend een offerte aan."
      />
      <Schema data={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: company.name,
        url: company.url,
      }} />
      <Schema data={{
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Diensten van Koning Aannemersbedrijf",
        itemListElement: [
          { service: "Woningrenovatie", slug: "complete-woning-renovatie" },
          { service: "Totaalrenovatie", slug: "complete-woning-renovatie" },
          { service: "Badkamer renovatie", slug: "badkamer-renovatie" },
          { service: "Toilet renovatie", slug: "toilet-renovatie" },
          { service: "Keuken renovatie", slug: "keuken-renovatie" },
          { service: "Zolderverbouwing", slug: "zolder-verbouwing" },
          { service: "Dakkapel", slug: "dakkapel" },
          { service: "Aanbouw", slug: "aanbouw" },
        ].map((s, i) => ({
          "@type": "ListItem", position: i + 1, name: s.service,
          url: `${company.url}/diensten/${s.slug}`,
        })),
      }} />
      <Schema data={{
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Woningrenovatie & Verbouwing",
        provider: { "@type": "GeneralContractor", name: company.name, url: company.url },
        areaServed: ["Maassluis", "Vlaardingen", "Schiedam", "Rotterdam", "Westland", "Den Haag", "Delft", "Zuid-Holland"],
        offers: { "@type": "Offer", priceCurrency: "EUR" },
      }} />

      {/* ============ HERO ============ */}
      <section className="relative bg-white overflow-hidden" data-testid="home-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 pt-12 md:pt-16 lg:pt-20 pb-20 md:pb-24">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            <div data-reveal>
              <span className="m-eyebrow mb-5 inline-block">Aannemer Maassluis · Zuid-Holland · 15+ jaar ervaring</span>
              <h1 className="text-[2.5rem] sm:text-5xl lg:text-[4.25rem] leading-[1.05] tracking-tight text-m-navy">
                Aannemer voor <span className="text-m-orange">complete woningrenovaties</span> in Zuid-Holland
              </h1>
              <p className="mt-7 text-m-text-2 text-base md:text-lg leading-relaxed max-w-xl">
                Koning Aannemersbedrijf is gespecialiseerd in totaalrenovaties, badkamers, toiletten en zolderverbouwingen. Eén aanspreekpunt, vaste totaalprijs en een vast uitvoeringsteam — werkzaam in Maassluis, Vlaardingen, Schiedam, Rotterdam, Westland, Den Haag, Delft en heel Zuid-Holland.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/offerte" className="btn-cta" data-testid="hero-cta-offerte">
                  Vrijblijvende offerte
                  <span className="btn-cta-icon"><ChevronRight size={14} /></span>
                </Link>
                <Link to="/projecten" className="btn-secondary" data-testid="hero-cta-projects">
                  Bekijk onze projecten <ArrowRight size={16} />
                </Link>
              </div>

              <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
                {trust.map((t) => (
                  <div key={t.label} className="flex items-center gap-2.5 text-m-text-2">
                    <t.icon size={18} className="text-m-orange" />
                    <span className="text-sm font-semibold">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative" data-reveal="2">
              <div className="relative aspect-[4/5] lg:aspect-[5/6] rounded-[28px] overflow-hidden shadow-soft-lg">
                <SmartImage src={heroImage} alt="Complete woningrenovatie in Delfshaven — project van Koning Aannemersbedrijf" aspect="5/6" eager className="absolute inset-0 w-full h-full object-cover" />
              </div>
              {/* Floating info card — ZM style */}
              <div className="absolute -bottom-8 -left-4 md:-left-10 right-6 md:right-12 bg-white rounded-[24px] p-6 md:p-7 shadow-soft-lg border border-m-border-soft" data-reveal="3">
                <div className="flex items-start gap-5">
                  <div className="shrink-0 w-12 h-12 rounded-2xl bg-m-orange-soft flex items-center justify-center">
                    <Star size={20} className="text-m-orange fill-m-orange" />
                  </div>
                  <div>
                    <div className="text-lg md:text-xl font-bold text-m-navy leading-tight">{displayRating ? `${displayRating} / 5 op Google` : "Beoordeeld op Google"}</div>
                    <div className="text-sm text-m-text-2 mt-1">{displayCount ? `Beoordeeld door ${displayCount} klanten op Google` : "Score wordt live opgehaald van Google"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STATS STRIP ============ */}
      <section className="bg-m-surface border-y border-m-border-soft" data-testid="home-stats">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-10 md:py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {[
              { v: "15+", l: "Jaar praktijkervaring" },
              { v: "VCA", l: "Gecertificeerde medewerkers" },
              { v: "Zuid-Holland", l: "Werkgebied" },
              { v: displayRating ? `${displayRating}★` : "Google", l: displayCount ? `Google-beoordeling (${displayCount} reviews)` : "Beoordelingen live op Google" },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-4xl md:text-5xl font-extrabold text-m-navy tracking-tight">{s.v}</div>
                <div className="mt-2 text-sm md:text-base text-m-text-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="bg-white section-y" data-testid="home-services">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-end mb-12 md:mb-16">
            <div>
              <span className="m-eyebrow mb-3 inline-block">Onze specialisaties</span>
              <h2 className="text-m-navy">
                Van strakke <Link to="/diensten/badkamer-renovatie" className="text-m-orange hover:underline" data-testid="home-h2-badkamer-link">badkamers</Link><br className="hidden md:inline" />
                tot complete <em className="not-italic text-m-petrol">verbouwingen</em>.
              </h2>
              <p className="mt-4 text-m-navy/70 max-w-xl leading-relaxed">
                Specialist in <Link to="/diensten/badkamer-renovatie" className="text-m-orange font-semibold hover:underline">badkamer renovatie</Link>: eigen loodgieter, elektricien en tegelzetter in één vast team, vaste prijs na gratis opname en uitvoering in circa twee weken.
              </p>
            </div>
            <Link to="/diensten" className="btn-secondary" data-testid="home-services-all">
              Alle diensten <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredServices.map((s) => (
              <Link
                key={s.slug}
                to={`/diensten/${s.slug}`}
                className="card-modern overflow-hidden group block"
                data-testid={`home-service-${s.slug}`}
              >
                <div className="relative aspect-[5/4] overflow-hidden">
                  <SmartImage src={s.image} alt={s.title} aspect="5/4" sizes="(max-width: 768px) 100vw, 33vw" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6 md:p-7">
                  <div className="text-xs font-semibold text-m-petrol uppercase tracking-wider mb-2">
                    {serviceCategories.find((c) => c.slug === s.category)?.title}
                  </div>
                  <h3 className="text-xl md:text-2xl text-m-navy">{s.title}</h3>
                  <p className="mt-3 text-sm text-m-text-2 line-clamp-2">{s.short}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-sm font-semibold text-m-petrol">Offerte op maat</span>
                    <span className="inline-flex items-center gap-1.5 text-m-orange font-semibold text-sm group-hover:gap-2.5 transition-all">
                      Bekijk <ArrowRight size={15} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BEFORE / AFTER ============ */}
      <section className="bg-m-surface section-y" data-testid="home-before-after">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-16 items-center">
          <div>
            <span className="m-eyebrow mb-3 inline-block">Voor & na</span>
            <h2 className="text-m-navy">
              Sleep voor het volledige <span className="text-m-orange">verschil</span>.
            </h2>
            <p className="mt-5 text-m-text-2 leading-relaxed max-w-md">
              Onze projecten spreken voor zich. Sleep de handle om voor en na te vergelijken — en zie hoe wij ruimtes compleet transformeren met vakmanschap.
            </p>
            <Link to="/projecten" className="btn-secondary mt-8" data-testid="home-portfolio-cta">
              Bekijk portfolio <ArrowRight size={16} />
            </Link>
          </div>
          <div className="rounded-[28px] overflow-hidden shadow-soft-lg bg-white p-2">
            <div className="rounded-[22px] overflow-hidden">
              <BeforeAfterSlider
                before={allProjects[0]?.before || ""}
                after={allProjects[0]?.after || ""}
                aspect="16/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============ ONZE WERKWIJZE (ZM-style 3 stappen) ============ */}
      <section className="bg-white section-y" data-testid="home-process">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-14 md:mb-16">
            <h2 className="text-m-navy mb-2">Onze werkwijze</h2>
            <span className="m-eyebrow">In 3 stappen naar uw renovatie</span>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {processSteps.map((p, i) => {
              const cardCls = p.variant === "orange"
                ? "card-orange"
                : p.variant === "tan"
                  ? "card-tan"
                  : "card-navy";
              return (
                <div key={p.num} className={`${cardCls} p-8 md:p-10 relative overflow-hidden min-h-[320px] flex flex-col`} data-reveal={String(i + 1)}>
                  <div className="absolute top-6 right-6 m-step-num leading-none">{p.num}</div>
                  <div className="relative z-10 mt-auto">
                    <h3 className="text-2xl md:text-3xl font-bold mb-3">{p.title}</h3>
                    <p className="leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE — USPs ============ */}
      <section className="bg-m-surface section-y" data-testid="home-why-choose">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-20 items-start">
          <div className="lg:sticky lg:top-28">
            <span className="m-eyebrow mb-3 inline-block">Waarom Koning Aannemersbedrijf</span>
            <h2 className="text-m-navy">
              Eén partij. <em className="not-italic text-m-orange">Vaste prijs.</em><br />
              Heldere afspraken.
            </h2>
            <p className="mt-5 text-m-text-2 leading-relaxed">
              Wij zijn aannemer van A tot Z. Eén aanspreekpunt, een vast uitvoeringsteam en strak projectmanagement. Premium kwaliteit zonder de chaos van losse partijen.
            </p>
            <Link to="/offerte" className="btn-cta mt-8" data-testid="why-choose-cta">
              Offerte aanvragen
              <span className="btn-cta-icon"><ChevronRight size={14} /></span>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {usps.map((u, i) => (
              <div key={u.t} className="card-modern p-7" data-reveal={String(i + 1)}>
                <div className="w-12 h-12 rounded-2xl bg-m-orange-soft flex items-center justify-center mb-5">
                  <u.icon size={22} className="text-m-orange" />
                </div>
                <h3 className="text-lg md:text-xl text-m-navy">{u.t}</h3>
                <p className="mt-2 text-sm leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CATEGORIES (SEO) ============ */}
      <section className="bg-white section-y" data-testid="home-categories">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12 md:mb-14">
            <span className="m-eyebrow mb-3 inline-block">Volledig overzicht</span>
            <h2 className="text-m-navy max-w-3xl">
              Alle diensten van Koning Aannemersbedrijf
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 md:gap-8">
            {serviceCategories.map((c) => (
              <div key={c.slug} className="border-t-2 border-m-orange pt-5">
                <Link to={`/diensten#${c.slug}`} className="block text-xl font-bold text-m-navy hover:text-m-orange transition-colors">
                  {c.title}
                </Link>
                <ul className="mt-4 space-y-2.5">
                  {(servicesGrouped[c.slug] || []).map((s) => (
                    <li key={s.slug}>
                      <Link to={`/diensten/${s.slug}`} className="text-sm text-m-text-2 hover:text-m-orange transition-colors" data-testid={`home-cat-${s.slug}`}>
                        {s.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ LOCATIONS ============ */}
      <section className="bg-m-surface section-y" data-testid="home-locations">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-end mb-12 md:mb-14">
            <div>
              <span className="m-eyebrow mb-3 inline-block">Werkgebied</span>
              <h2 className="text-m-navy">
                Premium aannemer in heel <span className="text-m-orange">Zuid-Holland</span>
              </h2>
            </div>
            <Link to="/locaties" className="btn-secondary">
              Alle locaties <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/locaties/${c.slug}`}
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-shadow"
                data-testid={`home-city-${c.slug}`}
              >
                <SmartImage src={c.image} alt={`Aannemer ${c.name}`} aspect="4/5" sizes="(max-width: 768px) 50vw, 16vw" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-m-navy/90 via-m-navy/30 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <div className="text-xl md:text-2xl font-bold text-white">{c.name}</div>
                  <div className="text-xs font-semibold text-m-orange mt-1">Zuid-Holland</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      <section className="bg-white section-y" data-testid="home-reviews">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="m-eyebrow mb-3 inline-block">Klantbeoordelingen</span>
              <h2 className="text-m-navy">Onze klanten over Koning</h2>
            </div>
            <div className="flex items-center gap-4" data-testid="home-reviews-rating">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={22} className="text-m-orange fill-m-orange" />
                ))}
              </div>
              <div>
                <div className="text-lg font-bold text-m-navy">{displayRating ? `${displayRating} / 5` : "Google"}</div>
                <div className="text-xs text-m-text-2">
                  {displayCount ? `${displayCount} Google-reviews` : "Beoordelingen op Google"}
                  {liveConfigured && <span className="ml-1 text-m-orange font-medium">· live</span>}
                </div>
              </div>
            </div>
          </div>
          {mergedReviews.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {mergedReviews.slice(0, 3).map((r, i) => (
              <article key={r.id} className="card-modern p-7 md:p-8 flex flex-col" data-testid={`home-review-${r.id}`} data-reveal={String(i + 1)}>
                <div className="flex mb-4">
                  {Array.from({ length: r.rating || 5 }).map((_, k) => (
                    <Star key={k} size={16} className="text-m-orange fill-m-orange" />
                  ))}
                </div>
                <p className="text-m-text leading-relaxed text-base flex-1">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-6 pt-5 border-t border-m-border-soft flex items-center gap-3">
                  {r.avatar && (
                    <img src={r.avatar} alt={r.name} className="w-9 h-9 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-m-navy truncate">{r.name}</div>
                    <div className="text-xs text-m-text-2 mt-0.5 truncate">
                      {[r.city, r.project].filter(Boolean).join(" · ") || `${r.source || "Google"}${r.date ? " · " + r.date : ""}`}
                    </div>
                  </div>
                </div>
              </article>
              ))}
            </div>
          ) : (
            <p className="text-m-text-2 leading-relaxed" data-testid="home-reviews-fallback">
              Onze Google-reviews worden live opgehaald en verschijnen hier automatisch. Bekijk alle beoordelingen op onze reviewpagina.
            </p>
          )}
          <div className="mt-10 text-center">
            <Link to="/reviews" className="btn-secondary">
              Alle reviews bekijken <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ LEAD MAGNET ============ */}
      <section className="bg-m-tan-soft section-y" data-testid="home-lead-magnet">
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-20">
          <LeadMagnetForm variant="banner" sourcePage="/home" />
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="bg-white section-y" data-testid="home-faq">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-10 text-center">
            <h2 className="text-m-navy mb-2">Veelgestelde vragen</h2>
            <span className="m-eyebrow">Alles wat u wilt weten</span>
          </div>
          <ModernFaq items={generalFaqs.slice(0, 6)} />
          <div className="mt-10 text-center">
            <Link to="/faq" className="btn-secondary" data-testid="home-faq-all">
              Alle vragen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-white pb-20 md:pb-28">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="card-navy p-10 md:p-16 lg:p-20 text-center md:text-left relative overflow-hidden">
            <div className="relative z-10 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <h2 className="text-white">
                  Klaar om uw woning te <span className="text-m-orange">transformeren?</span>
                </h2>
                <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
                  Vraag een vrijblijvende offerte aan. Binnen één werkdag persoonlijk contact.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3">
                <Link to="/offerte" className="btn-cta justify-center" data-testid="final-cta-offerte">
                  Offerte aanvragen
                  <span className="btn-cta-icon"><ChevronRight size={14} /></span>
                </Link>
                <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors backdrop-blur" data-testid="final-cta-phone">
                  <Phone size={16} /> {company.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* ============ MODERN FAQ ACCORDION (local) ============ */
const ModernFaq = ({ items }) => {
  const [open, setOpen] = React.useState(0);
  return (
    <div className="divide-y divide-m-border" data-testid="modern-faq">
      {items.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={f.q || f.question || i} className="py-2">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center justify-between gap-6 py-5 text-left group"
              data-testid={`modern-faq-item-${i}`}
            >
              <span className={`text-base md:text-lg font-bold transition-colors ${isOpen ? "text-m-orange" : "text-m-navy group-hover:text-m-orange"}`}>
                {f.q}
              </span>
              <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${isOpen ? "bg-m-orange text-white rotate-45" : "bg-m-surface text-m-navy"}`}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
            </button>
            <div className="overflow-hidden transition-all duration-300 ease-out" style={{ maxHeight: isOpen ? 600 : 0 }}>
              <p className="text-m-text-2 leading-relaxed pb-6 pr-12 text-sm md:text-base">{f.a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
